import { Link, useRouterState } from '@tanstack/react-router';
import {
  Drawer,
  IconButton,
  IcnBell,
  IcnBookOpen,
  IcnBookmark,
  IcnComment,
  IcnDoc,
  IcnDownload,
  IcnFolder,
  IcnGear,
  IcnHome,
  IcnKey,
  IcnSearch,
  IcnStopwatch,
  IcnUser,
} from '@viquard/doc-ui';
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface NavItem {
  id: string;
  label: string;
  icon: ReactElement;
  to: string;
}

interface NavSection {
  id: string;
  label?: string;
  items: NavItem[];
}

export interface MobileNavTriggerProps {
  authenticated?: boolean;
}

const ICON_SIZE = 16;

/** Hamburger affordance shown in the topbar's left island on viewports
 *  where the desktop center-pinned `<TopbarNav />` is hidden by
 *  `@viquard/doc-ui`'s layout (≤1023 px). Tapping it opens a left-side
 *  Drawer with the same nav links so phone / tablet visitors can still
 *  reach Articles / Calendar / Groups without going through the URL bar.
 *
 *  Why we host this in `apps/web/src/components/topbar/` (vs. inside
 *  doc-ui's AppShell): the items themselves are product-specific
 *  (`/articles`, `/calendar`, ...). doc-ui owns geometry; the consumer
 *  owns the link list — same split as `<TopbarNav />`.
 *
 *  Visibility is CSS-driven (`.topbar-mobile-trigger`): we render the
 *  button on every viewport and let the stylesheet hide it ≥1024 px so
 *  jsdom unit tests still find every node and there's no SSR/CSR
 *  mismatch when the desktop layout boots first. */
export function MobileNavTrigger({
  authenticated = false,
}: MobileNavTriggerProps): ReactNode {
  const { t } = useTranslation('common');
  const { location } = useRouterState();
  const [open, setOpen] = useState(false);

  // Auto-close the drawer on every navigation. TanStack Router doesn't
  // unmount the topbar between routes, so a Link tap inside the drawer
  // would otherwise leave it visibly open over the destination page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const publicItems: NavItem[] = [
    { id: 'home', label: t('nav.home'), icon: <IcnHome size={ICON_SIZE} />, to: '/' },
    { id: 'search', label: t('search.label'), icon: <IcnSearch size={ICON_SIZE} />, to: '/search' },
    { id: 'articles', label: t('nav.articles'), icon: <IcnDoc size={ICON_SIZE} />, to: '/articles' },
    { id: 'forum', label: t('nav.forum'), icon: <IcnComment size={ICON_SIZE} />, to: '/forum' },
    { id: 'surveys', label: t('nav.surveys'), icon: <IcnBookmark size={ICON_SIZE} />, to: '/surveys' },
    {
      id: 'calendar',
      label: t('nav.calendar'),
      icon: <IcnStopwatch size={ICON_SIZE} />,
      to: '/calendar',
    },
    { id: 'groups', label: t('nav.groups'), icon: <IcnUser size={ICON_SIZE} />, to: '/groups' },
  ];
  const sections: NavSection[] = authenticated
    ? [
        {
          id: 'account',
          label: t('userMenu.accountSection'),
          items: [
            {
              id: 'dashboard',
              label: t('nav.dashboard'),
              icon: <IcnHome size={ICON_SIZE} />,
              to: '/dashboard',
            },
            {
              id: 'profile',
              label: t('nav.profile'),
              icon: <IcnUser size={ICON_SIZE} />,
              to: '/profile',
            },
            {
              id: 'notifications',
              label: t('nav.notifications'),
              icon: <IcnBell size={ICON_SIZE} />,
              to: '/notifications',
            },
            {
              id: 'settings',
              label: t('nav.settings'),
              icon: <IcnGear size={ICON_SIZE} />,
              to: '/settings',
            },
          ],
        },
        {
          id: 'navigation',
          label: t('userMenu.navigationSection'),
          items: [
            {
              id: 'search',
              label: t('search.label'),
              icon: <IcnSearch size={ICON_SIZE} />,
              to: '/search',
            },
            ...publicItems.filter((item) => item.id !== 'home' && item.id !== 'search'),
          ],
        },
        {
          id: 'library',
          label: t('userMenu.readingSection'),
          items: [
            {
              id: 'library',
              label: t('nav.library'),
              icon: <IcnFolder size={ICON_SIZE} />,
              to: '/library',
            },
            {
              id: 'downloads',
              label: t('nav.downloads'),
              icon: <IcnDownload size={ICON_SIZE} />,
              to: '/library/downloads',
            },
            {
              id: 'read-later',
              label: t('nav.readLater'),
              icon: <IcnBookOpen size={ICON_SIZE} />,
              to: '/read-later',
            },
            {
              id: 'bookmarks',
              label: t('nav.bookmarks'),
              icon: <IcnBookmark size={ICON_SIZE} />,
              to: '/bookmarks',
            },
          ],
        },
        {
          id: 'site',
          label: t('userMenu.siteSection'),
          items: [
            {
              id: 'members',
              label: t('nav.members'),
              icon: <IcnUser size={ICON_SIZE} />,
              to: '/members',
            },
            {
              id: 'parent',
              label: t('nav.parent'),
              icon: <IcnKey size={ICON_SIZE} />,
              to: '/parent',
            },
          ],
        },
      ]
    : [{ id: 'public', items: publicItems }];

  return (
    <>
      <IconButton
        type="button"
        className="topbar-mobile-trigger"
        size="md"
        variant="ghost"
        label={t('nav.openMobileMenu')}
        onClick={() => { setOpen(true); }}
      >
        <HamburgerIcon />
      </IconButton>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        side="left"
        size="auto"
        title={t('nav.primary')}
        aria-label={t('nav.primary')}
      >
        <nav className="topbar-mobile-nav" aria-label={t('nav.primary')}>
          {authenticated ? (
            <Link
              to="/articles/new"
              className="topbar-mobile-nav__cta"
              onClick={() => { setOpen(false); }}
            >
              {t('nav.compose')}
            </Link>
          ) : (
            <div className="topbar-mobile-nav__guest-actions">
              <Link
                to="/register"
                className="topbar-mobile-nav__cta"
                onClick={() => { setOpen(false); }}
              >
                {t('auth.createAccount')}
              </Link>
              <Link
                to="/login"
                className="topbar-mobile-nav__secondary"
                onClick={() => { setOpen(false); }}
              >
                {t('auth.signIn')}
              </Link>
            </div>
          )}
          {sections.map((section) => (
            <div key={section.id} className="topbar-mobile-nav__section">
              {section.label !== undefined ? (
                <div className="topbar-mobile-nav__section-label">{section.label}</div>
              ) : null}
              <ul className="topbar-mobile-nav__list" role="list">
                {section.items.map((item) => {
                  const active = matchActive(location.pathname, item.to);
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.to}
                        aria-current={active ? 'page' : undefined}
                        className="topbar-mobile-nav__link"
                        onClick={() => { setOpen(false); }}
                      >
                        <span aria-hidden className="topbar-mobile-nav__icon">{item.icon}</span>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </Drawer>
    </>
  );
}

/** Three-stripe glyph. Inline SVG so we don't pull in a new doc-ui
 *  icon file for a single use site. Stroke width matches doc-ui's
 *  Icn* family at the 16-px tier. */
function HamburgerIcon(): ReactNode {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function matchActive(currentPath: string, target: string): boolean {
  if (target === '/dashboard') return currentPath === '/dashboard';
  if (target === '/') return currentPath === '/';
  return currentPath === target || currentPath.startsWith(`${target}/`);
}
